import { Link, Text } from '@obvio/app'

import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

const dateFormatter = new Intl.DateTimeFormat('en', {
  month: 'short',
  year: 'numeric',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

type DateObj = {
  month: string
  year: string
  day: string
}

function buildLocalTime(
  startTime?: string | undefined,
  endTime?: string | undefined,
): string {
  const haveBoth = startTime && endTime
  const separator = haveBoth ? ' - ' : ' '
  if (haveBoth) {
    return startTime + separator + endTime
  } else if (startTime) {
    return startTime
  } else if (endTime) {
    return `to ${endTime}`
  }

  return ''
}

function buildDateInfo(
  date: Date | string,
  {
    startTime,
    endTime,
  }: { startTime?: string | undefined; endTime?: string | undefined },
): string {
  const d = dateFormatter.formatToParts(new Date(date))
  const { month, year, day } = d.reduce<DateObj>(
    (acc, obj) => ({ ...acc, [obj.type]: obj.value }),
    { month: '0', year: '0', day: '0' },
  )

  const localTime = buildLocalTime(startTime, endTime)

  return `${localTime} ${month.toUpperCase()} ${day}, ${year}`
}

type EventHeadingProps = AllowUndefinedForOptional<{
  title: string
  date: string
  place?: string
  placeUrl?: string
  startTime?: string
  endTime?: string
  small?: boolean
}>

export function EventHeading({
  title,
  date,
  place,
  placeUrl,
  small,
  startTime,
  endTime,
}: EventHeadingProps): ReactElement {
  const dateInfo = buildDateInfo(date, {
    startTime,
    endTime,
  })
  return (
    <>
      <Text tag="h1" as={small ? 'h4' : 'h2'}>
        {title}
      </Text>
      <Text>
        {dateInfo}
        {place ? ' | ' : null}
        {placeUrl && place ? (
          <Link href={placeUrl}>{place}</Link>
        ) : (
          place ?? null
        )}
      </Text>
    </>
  )
}
